import React, { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { makeStyles } from "tss-react/mui";
import { alpha } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  DeleteOutlined,
  Edit,
  PeopleAltOutlined,
  Search,
  WhatsApp as WhatsAppIcon,
  Upload,
} from "@mui/icons-material";

import api from "../../services/api";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

// ── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles()((theme) => ({
  content: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  tableContainer: {
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.03)
        : alpha(theme.palette.common.black, 0.02),
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
  },
  tableRow: {
    transition: "background-color 0.12s",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    "& td": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    "&:last-child td": {
      borderBottom: "none",
    },
  },
  contactAvatar: {
    width: 36,
    height: 36,
    fontSize: 13,
    fontWeight: 700,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  },
  contactName: {
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
  },
  waBtn: {
    color: "#25D366",
    "&:hover": {
      backgroundColor: alpha("#25D366", 0.08),
    },
  },
  editBtn: {
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.08),
    },
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    "& .MuiTablePagination-toolbar": {
      paddingLeft: theme.spacing(2),
    },
  },
}));

// ── Avatar with initials fallback ─────────────────────────────────────────────
const ContactAvatar = ({ contact, className }) => {
  const initials = (contact.name || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Avatar src={contact.profilePicUrl || undefined} className={className}>
      {!contact.profilePicUrl && initials}
    </Avatar>
  );
};

// ── Skeleton rows ─────────────────────────────────────────────────────────────
const SkeletonRows = ({ count = 5 }) =>
  Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      <TableCell sx={{ width: 52 }}>
        <Skeleton variant="circular" width={36} height={36} />
      </TableCell>
      <TableCell>
        <Skeleton width="55%" height={16} sx={{ mb: 0.5 }} />
        <Skeleton width="35%" height={13} />
      </TableCell>
      <TableCell>
        <Skeleton width="65%" height={14} />
      </TableCell>
      <TableCell>
        <Skeleton width="50%" height={14} />
      </TableCell>
      <TableCell>
        <Skeleton width="70%" height={14} />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
          <Skeleton variant="circular" width={30} height={30} />
          <Skeleton variant="circular" width={30} height={30} />
          <Skeleton variant="circular" width={30} height={30} />
        </Box>
      </TableCell>
    </TableRow>
  ));

// ── Main component ────────────────────────────────────────────────────────────
const Contacts = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  // MUI TablePagination uses 0-based page; API uses 1-based pageNumber
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/", {
          params: {
            searchParam,
            pageNumber: page + 1,
            pageSize: rowsPerPage,
          },
        });
        setContacts(data.contacts);
        setTotalCount(data.count);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchParam, page, rowsPerPage]);

  // Real-time socket updates
  useEffect(() => {
    const socket = openSocket();
    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContacts((prev) =>
          prev.map((c) => (c.id === data.contact.id ? data.contact : c))
        );
      }
      if (data.action === "create") {
        // Bump count; user can navigate to last page to see the new entry
        setTotalCount((n) => n + 1);
      }
      if (data.action === "delete") {
        setContacts((prev) => prev.filter((c) => c.id !== +data.contactId));
        setTotalCount((n) => Math.max(0, n - 1));
      }
    });
    return () => socket.disconnect();
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        userId: user?.id,
        status: "open",
      });
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
      // If we deleted the last item on a non-first page, go back one page
      if (contacts.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        // Refresh current page
        setPage((p) => p); // triggers useEffect via dependency
        setContacts((prev) => prev.filter((c) => c.id !== contactId));
        setTotalCount((n) => Math.max(0, n - 1));
      }
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      navigate(0);
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const isEmpty = !loading && contacts.length === 0;

  return (
    <MainContainer>
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      {/* ── Header ── */}
      <MainHeader>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Row 1: title + action buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2} noWrap>
                  {i18n.t("contacts.title")}
                </Typography>
                {totalCount > 0 && (
                  <Chip
                    label={totalCount.toLocaleString()}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: 11 }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
                {totalCount > 0
                  ? `${totalCount.toLocaleString()} contact${totalCount !== 1 ? "s" : ""} in your list`
                  : "Manage your contacts"}
              </Typography>
            </Box>

            {/* Desktop: full buttons | Mobile: icon-only buttons */}
            <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Upload />}
                onClick={() => { setDeletingContact(null); setConfirmOpen(true); }}
              >
                {i18n.t("contacts.buttons.import")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleOpenContactModal}
              >
                {i18n.t("contacts.buttons.add")}
              </Button>
            </Box>
            <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 0.5, flexShrink: 0 }}>
              <Tooltip title={i18n.t("contacts.buttons.import")}>
                <IconButton
                  color="primary"
                  onClick={() => { setDeletingContact(null); setConfirmOpen(true); }}
                  sx={{ border: 1, borderColor: "primary.main", borderRadius: 1.5 }}
                >
                  <Upload fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n.t("contacts.buttons.add")}>
                <IconButton
                  color="primary"
                  onClick={handleOpenContactModal}
                  sx={{ bgcolor: "primary.main", color: "#fff", borderRadius: 1.5, "&:hover": { bgcolor: "primary.dark" } }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Row 2: search — full width on all sizes */}
          <TextField
            size="small"
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              },
            }}
          />
        </Box>
      </MainHeader>

      {/* ── Content ── */}
      <Box className={classes.content}>
        {/* Empty state */}
        {isEmpty && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 14,
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PeopleAltOutlined sx={{ fontSize: 40, color: "primary.main" }} />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {searchParam ? "No contacts found" : "No contacts yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {searchParam
                  ? `No results for "${searchParam}"`
                  : "Add your first contact to start managing conversations"}
              </Typography>
              {!searchParam && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleOpenContactModal}
                >
                  {i18n.t("contacts.buttons.add")}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Table */}
        {(contacts.length > 0 || loading) && (
          <Paper elevation={0} className={classes.tableContainer}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell} sx={{ width: 52, pl: 2 }} />
                    <TableCell className={classes.tableHeadCell}>
                      {i18n.t("contacts.table.name")}
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      {i18n.t("contacts.table.whatsapp")}
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      WhatsApp Name
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      {i18n.t("contacts.table.email")}
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="right" sx={{ pr: 2 }}>
                      {i18n.t("contacts.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <SkeletonRows count={rowsPerPage > 10 ? 8 : 5} />
                  ) : (
                    contacts.map((contact) => (
                      <TableRow key={contact.id} className={classes.tableRow}>
                        <TableCell sx={{ pl: 2, width: 52 }}>
                          <ContactAvatar contact={contact} className={classes.contactAvatar} />
                        </TableCell>

                        <TableCell>
                          <Typography className={classes.contactName}>
                            {contact.name}
                          </Typography>
                          {contact.isGroup && (
                            <Chip
                              label="Group"
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: 10, mt: 0.25 }}
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace", fontSize: 13 }}
                          >
                            {contact.number || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                            {contact.pushName || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                            {contact.email || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ pr: 1.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
                            <Tooltip title="Start chat" arrow>
                              <IconButton
                                size="small"
                                className={classes.waBtn}
                                onClick={() => handleSaveTicket(contact.id)}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit" arrow>
                              <IconButton
                                size="small"
                                className={classes.editBtn}
                                onClick={() => hadleEditContact(contact.id)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Can
                              role={user.profile}
                              perform="contacts-page:deleteContact"
                              yes={() => (
                                <Tooltip title="Delete" arrow>
                                  <IconButton
                                    size="small"
                                    className={classes.deleteBtn}
                                    onClick={() => {
                                      setConfirmOpen(true);
                                      setDeletingContact(contact);
                                    }}
                                  >
                                    <DeleteOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              className={classes.pagination}
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 40, 60]}
              labelRowsPerPage="Per page:"
              showFirstButton
              showLastButton
            />
          </Paper>
        )}
      </Box>
    </MainContainer>
  );
};

export default Contacts;
